import { MeResponseSchema, type MeResponse } from '@saniplus/contracts';

import { getApiClientRuntime } from '../config';
import { deleteMeta, getMeta, setMeta } from '../storage/idb';

/**
 * Who is signed in, kept beside the device uuid and the locale in the same
 * `meta` store — and nothing else.
 *
 * ## Why it exists
 *
 * The access token lives in memory for the lifetime of the tab (D-05), so a
 * reload has only the refresh cookie, and exchanging that needs a network. For
 * an installed PWA the tab being gone is the normal case: the OS evicts it and
 * the worker reopens the app from the home screen, inside a plant, with no
 * signal. That cold start had nothing to render a shell from, so it landed on
 * a login screen that cannot work offline — over a punch of theirs still
 * sitting in the queue (I-7), invisible and unactionable because the queue is
 * scoped to a user id the store no longer knew.
 *
 * ## Why it is not a persisted session
 *
 * The record is a `MeResponse` — exactly what `GET /auth/me` returns, which
 * carries no credential of any kind — and it is re-validated against the
 * frozen schema on the way in *and* on the way out. Zod strips what the schema
 * does not name, so a token cannot enter this store even by accident, and a
 * hand-edited record cannot widen into a session on the way back.
 *
 * It proves nothing and grants nothing. A shell rehydrated from it holds
 * `accessToken: null` and `status: 'offline'`; every request still needs a
 * token the server minted, and the first refresh that reaches the server
 * either upgrades the session or ends it.
 *
 * All three functions are no-ops unless the host app named a database in
 * `ApiClientRuntime.sessionSnapshotDb`.
 */
const SESSION_SNAPSHOT_KEY = 'sessionSnapshot';

function snapshotDb(): string | undefined {
  return getApiClientRuntime().sessionSnapshotDb;
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export async function readSessionSnapshot(): Promise<MeResponse | null> {
  const dbName = snapshotDb();
  if (dbName === undefined) return null;
  const raw = await getMeta(dbName, SESSION_SNAPSHOT_KEY).catch(() => null);
  if (raw === null) return null;
  const parsed = MeResponseSchema.safeParse(parseJson(raw));
  return parsed.success ? parsed.data : null;
}

/**
 * Best-effort on purpose: a device that refuses the write (private mode, a
 * full quota) still has a working online session, and failing a login over a
 * convenience for the *next* cold start would be the worse trade.
 */
export async function writeSessionSnapshot(me: MeResponse): Promise<void> {
  const dbName = snapshotDb();
  if (dbName === undefined) return;
  const parsed = MeResponseSchema.safeParse(me);
  if (!parsed.success) return;
  await setMeta(dbName, SESSION_SNAPSHOT_KEY, JSON.stringify(parsed.data)).catch(() => undefined);
}

export async function clearSessionSnapshot(): Promise<void> {
  const dbName = snapshotDb();
  if (dbName === undefined) return;
  await deleteMeta(dbName, SESSION_SNAPSHOT_KEY).catch(() => undefined);
}
